import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaPlusCircle,
  FaRobot,
  FaTrashAlt,
  FaUpload,
  FaUserFriends
} from "react-icons/fa";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

const WorkerAgentsPage = () => {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("token"), []);
  const workerID = useMemo(() => localStorage.getItem("workerID"), []);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [importError, setImportError] = useState("");
  const [deletingSlug, setDeletingSlug] = useState("");
  const [agentToDelete, setAgentToDelete] = useState(null);

  const fetchAgents = useCallback(async () => {
    if (!token || !workerID) {
      setImportError("Worker login required to view imported agents.");
      setLoadingAgents(false);
      return;
    }

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.LIST}`,
        { headers: authHeaders }
      );
      setAgents(Array.isArray(res.data?.agents) ? res.data.agents : []);
      setImportError("");
    } catch (err) {
      setImportError(err.response?.data?.message || "Failed to load worker agents.");
    } finally {
      setLoadingAgents(false);
    }
  }, [authHeaders, token, workerID]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setImportStatus("");
    setImportError("");

    if (!file) {
      setSelectedFileName("");
      setSelectedPackage(null);
      return;
    }

    try {
      const raw = await file.text();
      setSelectedPackage(JSON.parse(raw));
      setSelectedFileName(file.name);
    } catch {
      setSelectedPackage(null);
      setSelectedFileName(file.name);
      setImportError("Invalid JSON file. Please select a valid agent package.");
    }
  };

  const handleImport = async () => {
    if (!selectedPackage) {
      setImportError("Choose a valid JSON package first.");
      return;
    }

    setIsImporting(true);
    setImportError("");
    setImportStatus("");

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.IMPORT}`,
        { package: selectedPackage },
        { headers: authHeaders }
      );

      setImportStatus(res.data?.message || "Agent imported.");
      setSelectedPackage(null);
      setSelectedFileName("");
      await fetchAgents();
    } catch (err) {
      const details = err.response?.data?.errors;
      setImportError(
        Array.isArray(details) && details.length
          ? details.join(" ")
          : err.response?.data?.message || "Failed to import package."
      );
    } finally {
      setIsImporting(false);
    }
  };

  const confirmDelete = async () => {
    if (!agentToDelete?.slug) return;

    setDeletingSlug(agentToDelete.slug);
    setImportError("");
    setImportStatus("");

    try {
      await axios.delete(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.DELETE(agentToDelete.slug)}`,
        { headers: authHeaders }
      );
      await fetchAgents();
      setImportStatus(`Deleted configuration: ${agentToDelete.name}`);
    } catch (err) {
      setImportError(err.response?.data?.message || "Failed to delete configuration.");
    } finally {
      setDeletingSlug("");
      setAgentToDelete(null);
    }
  };

  return (
    <WorkerSidebarShell activeItem="agents">
      <div style={page}>
        <div style={hero}>
          <div>
            <div style={eyebrow}>Worker Agent Library</div>
            <h1 style={title}>Import and manage the agents you use with residents</h1>
            <p style={subtitle}>
              This page is only for configuration. To run an agent, open a resident in
              <strong> People I Support</strong> and jump into the workspace from there.
            </p>
          </div>
          <button
            type="button"
            style={secondaryButton}
            onClick={() => navigate("/activatedpatients")}
          >
            <FaUserFriends /> Go To People I Support
          </button>
        </div>

        <div style={layout}>
          <section style={panel}>
            <h2 style={sectionTitle}>Import Agent Configuration</h2>
            <p style={helperText}>
              Upload a `kinkeepers_agent` JSON export. If the slug already exists in your
              library, the configuration is updated in place.
            </p>

            <div style={uploadRow}>
              <label style={filePicker}>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <FaPlusCircle />
                <span>{selectedFileName || "Choose JSON file"}</span>
              </label>

              <button
                type="button"
                style={primaryButton}
                onClick={handleImport}
                disabled={isImporting || !selectedPackage}
              >
                <FaUpload />
                {isImporting ? "Importing..." : "Import"}
              </button>
            </div>

            {importStatus ? <p style={successText}>{importStatus}</p> : null}
            {importError ? <p style={errorText}>{importError}</p> : null}
          </section>

          <section style={panel}>
            <div style={sectionHeader}>
              <div>
                <h2 style={sectionTitle}>Your Agent Library</h2>
                <p style={helperText}>
                  Every agent here is available when you open a connected resident.
                </p>
              </div>
              <div style={countPill}>
                <FaRobot />
                <span>{agents.length} agent{agents.length === 1 ? "" : "s"}</span>
              </div>
            </div>

            {loadingAgents ? <p style={helperText}>Loading agents...</p> : null}
            {!loadingAgents && agents.length === 0 ? (
              <div style={emptyState}>
                <p style={emptyTitle}>No worker agents added yet.</p>
                <p style={helperText}>
                  Import one above, then open any resident you support to use it.
                </p>
              </div>
            ) : null}

            <div style={agentGrid}>
              {agents.map((agent) => (
                <article key={agent.slug} style={agentCard}>
                  <div style={agentHeader}>
                    <div style={agentIconWrap}>
                      <FaRobot />
                    </div>
                    <div>
                      <h3 style={agentName}>{agent.name}</h3>
                      <p style={agentSlug}>{agent.slug}</p>
                    </div>
                  </div>

                  <p style={agentDescription}>
                    {agent.description || "No description provided."}
                  </p>

                  <div style={metaRow}>
                    <span>Model: {agent.model || "N/A"}</span>
                    <span>Tools: {agent.tools?.length || 0}</span>
                    <span>Version: {agent.version || "N/A"}</span>
                  </div>

                  <div style={agentActions}>
                    <button
                      type="button"
                      style={ghostButton}
                      onClick={() => navigate("/activatedpatients")}
                    >
                      Use From Elder View
                    </button>
                    <button
                      type="button"
                      style={dangerButton}
                      onClick={() => setAgentToDelete(agent)}
                      disabled={deletingSlug === agent.slug}
                    >
                      <FaTrashAlt />
                      {deletingSlug === agent.slug ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        {agentToDelete ? (
          <div style={modalOverlay}>
            <div style={modalCard}>
              <h3 style={modalTitle}>Delete Agent Configuration</h3>
              <p style={modalText}>
                Delete "<strong>{agentToDelete.name}</strong>" from your worker library?
                This also removes its saved chat threads.
              </p>
              <div style={modalActions}>
                <button type="button" style={modalCancelButton} onClick={() => setAgentToDelete(null)}>
                  Cancel
                </button>
                <button type="button" style={modalDeleteButton} onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </WorkerSidebarShell>
  );
};

export default WorkerAgentsPage;

const page = {
  display: "grid",
  gap: "18px"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap"
};

const eyebrow = {
  color: "#0E5580",
  fontSize: "13px",
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  marginBottom: "10px"
};

const title = {
  margin: 0,
  color: "#12344d",
  fontSize: "30px",
  lineHeight: 1.15,
  maxWidth: "760px"
};

const subtitle = {
  margin: "10px 0 0",
  color: "#4f6475",
  maxWidth: "760px",
  lineHeight: 1.6
};

const layout = {
  display: "grid",
  gap: "16px"
};

const panel = {
  background: "#fff",
  border: "1px solid #deebf3",
  borderRadius: "16px",
  padding: "22px",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)"
};

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "14px"
};

const sectionTitle = {
  margin: 0,
  color: "#0E5580",
  fontSize: "22px"
};

const helperText = {
  marginTop: "8px",
  marginBottom: 0,
  color: "#5d7182",
  lineHeight: 1.5
};

const uploadRow = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: "18px"
};

const filePicker = {
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  border: "1px dashed #9cc2db",
  background: "#f5fbff",
  color: "#184f73",
  borderRadius: "12px",
  padding: "12px 14px",
  cursor: "pointer",
  minWidth: "240px"
};

const primaryButton = {
  border: "none",
  background: "#0E5580",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 16px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  fontWeight: 700
};

const secondaryButton = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#0E5580",
  borderRadius: "10px",
  padding: "12px 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  fontWeight: 700
};

const countPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 12px",
  borderRadius: "999px",
  background: "#edf7ff",
  color: "#0E5580",
  fontSize: "13px",
  fontWeight: 700
};

const successText = {
  marginTop: "16px",
  marginBottom: 0,
  color: "#1f7a33",
  fontWeight: 600
};

const errorText = {
  marginTop: "16px",
  marginBottom: 0,
  color: "#b42318",
  fontWeight: 600
};

const emptyState = {
  border: "1px dashed #c6d8e6",
  borderRadius: "14px",
  background: "#f8fbfd",
  padding: "18px"
};

const emptyTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "17px",
  fontWeight: 700
};

const agentGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "14px"
};

const agentCard = {
  border: "1px solid #deebf3",
  borderRadius: "14px",
  background: "#fcfeff",
  padding: "16px",
  display: "grid",
  gap: "14px"
};

const agentHeader = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const agentIconWrap = {
  width: "44px",
  height: "44px",
  borderRadius: "12px",
  background: "#edf7ff",
  color: "#0E5580",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px"
};

const agentName = {
  margin: 0,
  color: "#12344d",
  fontSize: "18px"
};

const agentSlug = {
  margin: "4px 0 0",
  color: "#6b7f90",
  fontSize: "12px"
};

const agentDescription = {
  margin: 0,
  color: "#4f6475",
  lineHeight: 1.5,
  minHeight: "48px"
};

const metaRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  color: "#5d7182",
  fontSize: "12px"
};

const agentActions = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap"
};

const ghostButton = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#0E5580",
  borderRadius: "10px",
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600
};

const dangerButton = {
  border: "1px solid #f1c3bf",
  background: "#fff5f4",
  color: "#b42318",
  borderRadius: "10px",
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px"
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.58)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  zIndex: 9999
};

const modalCard = {
  background: "#fff",
  borderRadius: "16px",
  padding: "24px",
  maxWidth: "420px",
  width: "100%",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.24)"
};

const modalTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "20px"
};

const modalText = {
  margin: "12px 0 0",
  color: "#4f6475",
  lineHeight: 1.5
};

const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "20px"
};

const modalCancelButton = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#4f6475",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 600
};

const modalDeleteButton = {
  border: "none",
  background: "#b42318",
  color: "#fff",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 700
};
